/**
 * /add-device?token=… — register a passkey on a SECOND device (V072, PR 3).
 *
 * The other end of both device-link flows: the emailed "no passkey on this
 * device?" link, and the QR minted from an already-signed-in profile. Either
 * way the token authorises adding a credential to an account that already
 * exists — it never creates one, and it cannot sign anybody in on its own.
 *
 * Until this page existed a client could hold exactly ONE passkey: registration
 * is reachable only through a signup token, and that token is refused the
 * moment the account has a credential. A new phone meant a lost account.
 */

import { registerPasskey, isPasskeySupported } from "@re-platform/auth";
import type { PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/types";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { APP_NAME } from "../../brand";
import { passkeyApi } from "./passkeyApi";
import { saveSession } from "./session";
import "./SignIn.css";
import { PublicFooter } from "../../shell/PublicFooter";

type Status =
  | { kind: "idle" }
  | { kind: "loading"; stage: "begin" | "prompt" | "finish" }
  | { kind: "error"; message: string }
  | { kind: "done" };

/** A hint at which device this is, so the list later reads as devices not rows. */
function guessDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  return "This device";
}

export function AddDevice() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [supported, setSupported] = useState<boolean | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const navigate = useNavigate();

  useEffect(() => {
    setSupported(isPasskeySupported());
  }, []);

  async function addThisDevice(): Promise<void> {
    setStatus({ kind: "loading", stage: "begin" });
    try {
      const begin = await passkeyApi.deviceLinkBegin({
        device_link_token: token,
      });

      setStatus({ kind: "loading", stage: "prompt" });
      const result = await registerPasskey(
        begin.public_key as unknown as PublicKeyCredentialCreationOptionsJSON,
      );

      setStatus({ kind: "loading", stage: "finish" });
      // The server signs this device in as part of finishing: account ownership
      // (the token) and device possession (the biometric just answered) are both
      // already proven, so a second prompt would be ceremony, not security.
      const finish = await passkeyApi.deviceLinkFinish({
        challenge_id: begin.challenge_id,
        attestation: result.response as unknown as Record<string, unknown>,
        device_link_token: token,
        friendly_name: guessDeviceName(),
      });

      saveSession({
        accessToken: finish.access_token,
        refreshToken: finish.refresh_token,
        expiresIn: finish.expires_in,
        userId: finish.user_id,
        tenantId: finish.tenant_id,
        primaryRole: finish.primary_role,
        email: finish.email,
        displayName: finish.display_name,
      });
      setStatus({ kind: "done" });
      navigate("/");
    } catch (err) {
      setStatus({
        kind: "error",
        message:
          err instanceof Error ? err.message : "Couldn't add this device.",
      });
    }
  }

  if (!token) {
    return (
      <main className="signin">
        <section className="signin__card">
          <h1>That link is incomplete</h1>
          <p>
            Open the link from your email exactly as it was sent, or scan the QR
            code again from your other device.
          </p>
          <p className="signin__footnote">
            <Link to="/login">Back to sign in</Link>
          </p>
        </section>
        <PublicFooter />
      </main>
    );
  }

  if (supported === false) {
    return (
      <main className="signin">
        <section className="signin__card">
          <h1>Passkeys aren't supported here</h1>
          <p>
            This device's browser doesn't support WebAuthn passkeys. Try the
            latest Chrome, Safari, Firefox, or Edge on a device with Touch ID,
            Face ID, Windows Hello, or a security key.
          </p>
        </section>
        <PublicFooter />
      </main>
    );
  }

  return (
    <main className="signin">
      <section className="signin__card">
        <div className="signin__brand">
          <KeyRound size={28} aria-hidden />
          <span>{APP_NAME}</span>
        </div>
        <h1>Add this device</h1>
        <p className="signin__subtitle">
          Set up Face ID, your fingerprint, or your device PIN here. Your other
          devices keep working — this adds one, it doesn't replace anything.
        </p>

        <button
          type="button"
          className="signin__cta rp-btn rp-btn--primary"
          disabled={status.kind === "loading" || status.kind === "done"}
          onClick={() => void addThisDevice()}
        >
          {status.kind === "loading" ? (
            <>
              <Loader2 className="signin__spin" size={18} aria-hidden />
              {status.stage === "begin" && "Preparing…"}
              {status.stage === "prompt" && "Confirm with your device…"}
              {status.stage === "finish" && "Finishing up…"}
            </>
          ) : (
            <>
              <ShieldCheck size={18} aria-hidden />
              Add this device
            </>
          )}
        </button>

        {status.kind === "error" && (
          <div className="signin__error" role="alert">
            <strong>That didn't work.</strong>
            <p>{status.message}</p>
            <p className="signin__hint">
              These links expire after 15 minutes and work only once. Ask for a
              fresh one from the <Link to="/login">sign-in page</Link>.
            </p>
          </div>
        )}
      </section>
      <PublicFooter />
    </main>
  );
}
